import type { ColumnGroupType, ColumnType, ColumnsType } from 'antd/es/table';
import type { Key } from 'react';

import { FALLBACK_VIRTUAL_COLUMN_WIDTH } from './constants';
import type { HighlightedRow } from './types';

export const getVisibleColumnKeys = (
  orderedColumns: Array<{ key?: Key; hidden?: boolean }>,
  hiddenColumns?: string[]
): string[] =>
  orderedColumns
    .filter(
      col => !col.hidden && !hiddenColumns?.includes(String(col.key ?? ''))
    )
    .map(col => String(col.key ?? ''))
    .filter(Boolean);

/**
 * Sums the rendered width of the currently visible columns so a virtual table
 * can be given a `scroll.x` that matches its real content width.
 *
 * AntD's virtual body requires a numeric `scroll.x` and uses it as the lower
 * bound for the grid width (`Math.max(scroll.x, containerWidth)`). When that
 * bound exceeds the real column total, rc-table scales *every* column up
 * proportionally to fill it — including sticky/fixed columns. A hardcoded
 * `scroll.x` therefore makes fixed columns stretch once enough columns are
 * hidden. Deriving the width from the visible columns keeps it in lockstep
 * with what is actually rendered, so no upscaling occurs.
 *
 * Hidden columns (and fully-hidden groups) are skipped; group columns are
 * flattened to their leaf children, mirroring the table's flat column model.
 */
export const getVisibleColumnsWidth = <T>(
  columns: ColumnsType<T> | undefined,
  selectionColumnWidth = 0
): number => {
  if (!columns?.length) return selectionColumnWidth;

  const sumColumns = (cols: ColumnsType<T>): number =>
    cols.reduce((total, col) => {
      if ((col as { hidden?: boolean }).hidden) return total;

      const children = (col as ColumnGroupType<T>).children;
      if (children?.length) return total + sumColumns(children);

      const { width } = col as ColumnType<T>;
      return (
        total +
        (typeof width === 'number' ? width : FALLBACK_VIRTUAL_COLUMN_WIDTH)
      );
    }, 0);

  return sumColumns(columns) + selectionColumnWidth;
};

export const isHighlightedRowObject = (
  item: Key | HighlightedRow
): item is HighlightedRow =>
  typeof item === 'object' && item !== null && 'key' in item;

export const getRowKey = (item: Key | HighlightedRow): Key =>
  isHighlightedRowObject(item) ? item.key : item;

/**
 * Stable `data-testid` applied to every table row (see TableView). Built from
 * the row's resolved `rowKey`, it gives automation a semantic per-row anchor —
 * `getByTestId('table-row-<key>')` — instead of filtering by row text or
 * relying on column order. Scope it through the table locator when a page
 * renders more than one table.
 */
export const getRowTestId = (key: Key): string => `table-row-${key}`;
